import './perks-post.component.scss';

import { h } from 'preact';
import { useHashLocation } from 'wouter-preact/use-hash-location';

import { useAuth } from '../../../../auth/hooks/use-auth.hook';
import { selectAccountSettings } from '../../../../auth/store/auth.selectors';
import { useTranslation } from '../../../../i18n';
import type { AccountSettingsResponse } from '../../../../models/common.model';
import type { NeedAccessReq } from '../../../../need-access/need-access.interface';
import { NeedAccessContentType } from '../../../../need-access/need-access.interface';
import { NeedAccessService } from '../../../../need-access/need-access.service';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import type { Post } from '../../../model/post.interface';
import type { UserActionRequest } from '../../../model/user-action.inteface';
import { postUserAction } from '../../../store/perks.thunk';
import {
  requestCommentInputFocus,
  requestCommentsScroll,
} from '../perks-post-comments/comment-entry';
import { PerksPostBody } from './perks-post-body.component';
import { PerksPostCreator } from './perks-post-creator.component';
import { PerksPostFooter } from './perks-post-footer.component';
import { PerksPostMedia } from './perks-post-media/perks-post-media.component';
import { useIntersectionObserver } from './userIntersectionObserver.hook';

interface Props {
  key: string;
  post: Post;
  previewMode?: boolean;
  showTopComment?: boolean;
  onJoinPreviewClicked?: () => void;
  collapseLongPosts?: boolean;
}

export const PerksPostComponent = ({
  post,
  previewMode = false,
  showTopComment = false,
  onJoinPreviewClicked,
  collapseLongPosts = true,
}: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [location, setLocation] = useHashLocation();
  const needAccessService = new NeedAccessService();
  const accountSettingsResp: AccountSettingsResponse = useAppSelector(selectAccountSettings);
  const { accountSettings } = accountSettingsResp || {};
  const { openAuthModal } = useAuth();

  const handleNeedAccess = async (): Promise<void> => {
    if (!post?.id) return;

    const req: NeedAccessReq = {
      contentId: post.id,
      contentType: NeedAccessContentType.POST,
    };
    await needAccessService.processNeedAccessRequest({ request: req, dispatch, openAuthModal });
  };

  const goToPost = async (comments = false, focusInput = false) => {
    if (!!post?.hidden || previewMode) {
      await handleNeedAccess();
    } else {
      if (comments) {
        requestCommentsScroll();
      }
      if (focusInput) {
        requestCommentInputFocus(t('perks.addComment'));
      }
      setLocation(`/posts/${post.id}`);
    }
  };

  const handleIntersection = (entries: any, observer: any) => {
    entries.forEach((entry: any) => {
      if (entry.isIntersecting && !post?.hidden) {
        const action = {
          postId: post?.id,
          actionType: 'VIEW',
        } as UserActionRequest;
        dispatch(postUserAction(action));
        observer.unobserve(entry.target);
      }
    });
  };

  const elementRef = useIntersectionObserver(handleIntersection, {
    threshold: 0.1,
  });

  return (
    <div class="single-post" data-post-id={post.id} ref={elementRef}>
      <PerksPostMedia
        post={post}
        showSlider={true}
        showPlayer={true}
        previewMode={previewMode ?? false}
        onJoinPreviewClick={onJoinPreviewClicked ?? undefined}
      />

      {(accountSettings?.showPostAuthor || (post.pinned && !previewMode)) && (
        <PerksPostCreator
          post={post}
          showPostAuthor={accountSettings?.showPostAuthor ?? false}
          goToPost={goToPost}
        />
      )}

      <PerksPostBody
        post={post}
        previewMode={previewMode ?? false}
        showPostDate={!previewMode && !accountSettings?.showPostAuthor}
        goToPost={goToPost}
        collapseLongPosts={collapseLongPosts}
      />

      <PerksPostFooter
        post={post}
        previewMode={previewMode ?? false}
        showTopComment={showTopComment ?? false}
        onNeedAccessClick={handleNeedAccess}
        goToPost={goToPost}
      />
    </div>
  );
};
